import React, { ReactNode, useState } from 'react';
import styled from '@emotion/styled';
import AnimateHeight from './AnimateHeight';
import classnames from '../../util/classnames';
import { darken } from 'polished';
import Icon from 'src/components/Icon';

type AccordionColor = 'primary' | 'info' | 'danger' | 'warning' | 'secondary';
type AccordionIcon = 'chevron' | 'plusminus';

type AccordionItemProps = {
  title: ReactNode;
  children: ReactNode;
  itemId?: string;
  id?: string;
  isOpen?: boolean;
  toggle?: (id?: string) => void;
  defaultOpen?: boolean;
  className?: string;
  titleClassName?: string;
  color?: AccordionColor;
  /** CSS color string for the left accent border (status/category indicator). Overrides the `color` border. */
  leftAccentColor?: string;
  /** CSS color string for a selection highlight: subtle bg tint + all-border. leftAccentColor still overrides the left side. */
  selectionBorderColor?: string;
  /** Content rendered before the title that does not trigger the accordion toggle on click. */
  prefixContent?: ReactNode;
  /** Icon style: chevron up/down or plus/minus. Defaults to plusminus. */
  icon?: AccordionIcon;
};

const StyledAccordionItem = styled('div', {
  shouldForwardProp: prop => prop !== 'leftAccentColor' && prop !== 'selectionBorderColor',
})<{ color?: AccordionColor; leftAccentColor?: string; selectionBorderColor?: string }>`
  ${props => {
    let styles = '';
    if (props.selectionBorderColor) {
      styles += `border-color: ${props.selectionBorderColor} !important; background-color: ${props.selectionBorderColor}15 !important;`;
    } else if (props.color) {
      const themeColor = (props?.theme as any)?.[`${props.color}Main`];
      const _color = darken(0.3, themeColor || props.color);
      styles += `border: 1px solid ${_color} !important;`;
    }
    if (props.leftAccentColor) {
      styles += ` border-left: 3px solid ${props.leftAccentColor} !important;`;
    }
    return styles;
  }}
  .accordion-header:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

export function AccordionItem({
  title,
  children,
  itemId = '',
  id,
  isOpen: isOpenProp,
  toggle,
  defaultOpen = false,
  className = '',
  titleClassName = '',
  color,
  leftAccentColor,
  selectionBorderColor,
  prefixContent,
  icon = 'plusminus',
}: AccordionItemProps) {
  const [selfOpen, setSelfOpen] = useState(defaultOpen);
  const isControlled = isOpenProp !== undefined;
  const isOpen = isControlled ? isOpenProp : selfOpen;

  const handleToggle = () => {
    if (isControlled && toggle) {
      toggle(itemId);
    } else {
      setSelfOpen(v => !v);
    }
  };

  const iconClass = icon === 'chevron' ? `fa ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'}` : `fa ${isOpen ? 'fa-minus' : 'fa-plus'}`;

  return (
    <StyledAccordionItem
      id={id}
      className={classnames(`border border-gray-200 dark:border-gray-500 rounded`, className)}
      color={color}
      leftAccentColor={leftAccentColor}
      selectionBorderColor={selectionBorderColor}
    >
      <div className="accordion-header flex items-center gap-2 px-2 py-2 bg-light rounded" style={{ cursor: 'pointer' }} onClick={handleToggle}>
        {prefixContent && (
          <div onClick={e => e.stopPropagation()} style={{ flexShrink: 0 }}>
            {prefixContent}
          </div>
        )}
        <span className={classnames('flex-grow py-1 px-1', titleClassName)} style={{ minWidth: 0 }}>
          {title}
        </span>
        <Icon icon={iconClass} style={{ flexShrink: 0, fontSize: '0.75rem' }} />
      </div>
      <AnimateHeight>{isOpen ? children : null}</AnimateHeight>
    </StyledAccordionItem>
  );
}

type AccordionProps = {
  children: ReactNode;
};

export function Accordion({ children }: AccordionProps) {
  const [openSection, setOpenSection] = useState('1');

  const toggle = (id?: string) => {
    setOpenSection(openSection === id ? '' : id ?? '');
  };

  return (
    <div className="custom-accordion">
      {React.Children.map(children as ReactNode, child => {
        if (!React.isValidElement(child)) return null;
        return React.cloneElement(child as React.ReactElement<any>, {
          isOpen: openSection === (child.props as any).itemId,
          toggle,
        });
      })}
    </div>
  );
}
